import type { ComplaintCategory, ComplaintStatus } from '@zeus/index'
import type { Complaint } from '~/gql/complaints/complaints'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { Label } from '~/components/ui/label'
import { COMPLAINT_CATEGORY_KEYS, COMPLAINT_ORIGIN_KEYS, COMPLAINT_STATUS_KEYS } from './complaint-labels'

const FIELD = 'rounded-md border border-input bg-transparent px-3 text-sm'

type IProps = {
  complaint: Complaint
  formatDate: (value: unknown) => string
  busy: boolean
  onCancel: () => void
  onSave: (input: { category: ComplaintCategory, status: ComplaintStatus, response: string }) => void
}

/** Traiter une entrée du registre (#325) : la lire, la classer, y répondre, la clore. */
function ComplaintDialog({ complaint, formatDate, busy, onCancel, onSave }: IProps) {
  const { t } = useTranslation()
  const [category, setCategory] = useState<ComplaintCategory>(complaint.category)
  const [status, setStatus] = useState<ComplaintStatus>(complaint.status)
  const [response, setResponse] = useState(complaint.response ?? '')

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    onSave({ category, status, response })
  }

  return (
    <Dialog open onOpenChange={open => !open && onCancel()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{complaint.subject}</DialogTitle>
            <DialogDescription>
              {t('complaints.receivedFrom', {
                date: formatDate(complaint.createdAt),
                author: complaint.authorName || t('complaints.author.none'),
                origin: t(COMPLAINT_ORIGIN_KEYS[complaint.origin]),
              })}
            </DialogDescription>
          </DialogHeader>
          <p className="whitespace-pre-line rounded-md bg-muted p-3 text-sm">{complaint.message}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="complaintCategory">{t('complaints.column.category')}</Label>
              <select id="complaintCategory" value={category} onChange={e => setCategory(e.target.value as ComplaintCategory)} className={`h-10 ${FIELD}`}>
                {Object.entries(COMPLAINT_CATEGORY_KEYS).map(([value, key]) => <option key={value} value={value}>{t(key)}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="complaintStatus">{t('complaints.column.status')}</Label>
              <select id="complaintStatus" value={status} onChange={e => setStatus(e.target.value as ComplaintStatus)} className={`h-10 ${FIELD}`}>
                {Object.entries(COMPLAINT_STATUS_KEYS).map(([value, key]) => <option key={value} value={value}>{t(key)}</option>)}
              </select>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="complaintResponse">{t('complaints.column.response')}</Label>
            <textarea id="complaintResponse" rows={4} maxLength={5000} value={response} onChange={e => setResponse(e.target.value)} aria-describedby="complaintResponseHelp" className={`py-2 ${FIELD}`} />
            <p id="complaintResponseHelp" className="text-xs text-muted-foreground">{t('complaints.responseHelp')}</p>
          </div>
          {(complaint.respondedAt || complaint.resolvedAt) && (
            <p className="text-xs text-muted-foreground">
              {[
                complaint.respondedAt && t('complaints.respondedOn', { date: formatDate(complaint.respondedAt) }),
                complaint.resolvedAt && t('complaints.resolvedOn', { date: formatDate(complaint.resolvedAt) }),
              ].filter(Boolean).join(' · ')}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={busy}>{t('common.save')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default ComplaintDialog
